// ── the event taxonomy ───────────────────────────────────────────────────────
//
// THE single vocabulary for everything we measure about how a person moves
// through innernet. One name per real moment in the journey — not a dumping
// ground, not a per-component habit. If a surface wants to record something, it
// adds a row HERE first and then fires it; that is the whole contract.
//
// Why a file and not just strings at the call sites: an analytics funnel is only
// as good as the consistency of its names. `connect_verified` fired from the web
// dashboard and `connect-verified` fired from an MCP route are two different
// events to PostHog, and the funnel silently reads as a cliff. Typing the names
// makes that class of mistake impossible to write.
//
// This module is DELIBERATELY PURE — no posthog-js, no posthog-node, no window,
// no env. That is what lets the browser wrapper (lib/analytics.ts) and the
// server wrapper (lib/analytics/server.ts) share one taxonomy instead of drifting
// into two. Each of them builds its own typed `track` from `createTypedTrack`
// below, so both ends of the product report into the same funnel.
//
// RENAMING IS DESTRUCTIVE. PostHog keys history by event name; renaming an event
// orphans every past occurrence and breaks the saved funnels built on it. The
// three names already live in the project — `begin_opened`, `signup_completed`,
// `signed_in` — are therefore frozen. Add, don't rewrite.

/** Loose property bag. PostHog accepts any JSON-ish value. */
export type AnalyticsProperties = Record<string, unknown>

export const EVENTS = {
  // ── acquisition · a stranger arrives ──────────────────────────────────────
  /** the public landing rendered — the top of every funnel we draw. */
  landing_viewed: 'landing_viewed',
  /** the begin/auth flow opened. EXISTING — do not rename. */
  begin_opened: 'begin_opened',

  // ── auth · the stranger becomes a person ──────────────────────────────────
  /** a credential was actually submitted (OAuth handoff, magic link sent). */
  signup_started: 'signup_started',
  /** a brand-new account has a session. EXISTING — do not rename. */
  signup_completed: 'signup_completed',
  /** a returning account has a session. EXISTING — do not rename. */
  signed_in: 'signed_in',
  /** auth came back with an error — the silent half of the auth step. */
  auth_failed: 'auth_failed',

  // ── onboarding · the guided first minutes ─────────────────────────────────
  onboarding_started: 'onboarding_started',
  onboarding_step_viewed: 'onboarding_step_viewed',
  onboarding_step_completed: 'onboarding_step_completed',
  /** left the flow early — where people bail is the point of measuring it. */
  onboarding_skipped: 'onboarding_skipped',
  onboarding_completed: 'onboarding_completed',
  /** netti mailed someone the note for the step they left at — server-side,
   *  from the lifecycle ritual. `campaign` says which step. */
  nudge_sent: 'nudge_sent',
  /** the house mailed someone whose memory crossed a cell threshold — server
   *  side, from the milestone ritual. `threshold` says which line, `cells` how
   *  far past it they were when we wrote. */
  milestone_sent: 'milestone_sent',

  // ── connection · the activation moment ────────────────────────────────────
  // Letting an AI in is the moment innernet stops being a website and starts
  // being memory. Everything before this is intent; everything after is usage.
  // These five are the highest-value events in the taxonomy — treat them as the
  // real conversion, not signup.
  connect_viewed: 'connect_viewed',
  connect_client_selected: 'connect_client_selected',
  connect_instructions_copied: 'connect_instructions_copied',
  /** a client actually called us — the activation itself, usually server-side. */
  connect_verified: 'connect_verified',
  connect_failed: 'connect_failed',

  // ── first value · the memory starts to exist ──────────────────────────────
  // Most of these fire from MCP, where there is no browser — see
  // lib/analytics/server.ts. They are the difference between "connected" and
  // "actually using it".
  project_created: 'project_created',
  memory_captured: 'memory_captured',
  commit_created: 'commit_created',
  artifact_started: 'artifact_started',
  import_started: 'import_started',
  import_completed: 'import_completed',

  // ── friction · where it goes wrong ────────────────────────────────────────
  // The events nobody wants to add and everybody needs. A funnel shows WHERE
  // people fall out; these three are the only thing that hints at WHY.
  /** a gate said no (beta, founders-only, plan limit). */
  feature_blocked: 'feature_blocked',
  /** an error surface was actually shown to a human. */
  error_shown: 'error_shown',
  /** a screen rendered with nothing in it — the quietest kind of dead end. */
  empty_state_viewed: 'empty_state_viewed',

  // ── the investor deck · a link we handed out, opened ──────────────────────
  // /investors/deck is a URL-only surface handed to named investors, so the
  // questions it has to answer are not the product funnel's. They are: who
  // opened it, when, and how long they actually held each view.
  //
  // `deck_view_read` is the load-bearing one. Its `seconds` counts VISIBLE time
  // only — a tab left open overnight is not interest, and a deck report that
  // treats it as interest is worse than no report. See useDeckObserver.
  /** the deck was opened — one per visit, not per view. */
  deck_opened: 'deck_opened',
  /** one view was left behind, with the attention it actually held. */
  deck_view_read: 'deck_view_read',
  /** the visit ended — totals for the walk, fired on pagehide. */
  deck_finished: 'deck_finished',
  /** one finished READ by a person, as mailed to the founders (server-side):
   *  seconds, reach, the slide held longest, steps, taps, netti questions, and
   *  `s_<view>` seconds per slide. Bots never produce one. */
  deck_session_report: 'deck_session_report',
} as const

/** Every legal event name. Call sites take this, so a typo is a compile error. */
export type AnalyticsEvent = (typeof EVENTS)[keyof typeof EVENTS]

// ── funnel stages ────────────────────────────────────────────────────────────
// The six stages the whole journey collapses into. Stamped onto every event by
// `funnelProps` so a single PostHog breakdown can answer "how far do people get"
// without anyone having to remember which events belong to which step.

export const FUNNEL_STAGES = [
  'acquisition',
  'auth',
  'onboarding',
  'connection',
  'first_value',
  'friction',
] as const

export type FunnelStage = (typeof FUNNEL_STAGES)[number]

/**
 * Which stage each event belongs to. Typed as a TOTAL record on purpose: add an
 * event to `EVENTS` without placing it in the funnel and this file stops
 * compiling. That is the guard rail — an unplaced event is an event nobody will
 * ever find in a report.
 */
export const EVENT_STAGE: Record<AnalyticsEvent, FunnelStage> = {
  landing_viewed: 'acquisition',
  begin_opened: 'acquisition',

  signup_started: 'auth',
  signup_completed: 'auth',
  signed_in: 'auth',
  auth_failed: 'auth',

  onboarding_started: 'onboarding',
  onboarding_step_viewed: 'onboarding',
  onboarding_step_completed: 'onboarding',
  onboarding_skipped: 'onboarding',
  onboarding_completed: 'onboarding',
  nudge_sent: 'onboarding',
  // past every rung: this one only ever goes to someone the product is already
  // working for, so it belongs with the events that prove that
  milestone_sent: 'first_value',

  connect_viewed: 'connection',
  connect_client_selected: 'connection',
  connect_instructions_copied: 'connection',
  connect_verified: 'connection',
  connect_failed: 'connection',

  project_created: 'first_value',
  memory_captured: 'first_value',
  commit_created: 'first_value',
  artifact_started: 'first_value',
  import_started: 'first_value',
  import_completed: 'first_value',

  feature_blocked: 'friction',
  error_shown: 'friction',
  empty_state_viewed: 'friction',

  // an investor arriving on a link we handed out is an arrival like any other.
  deck_opened: 'acquisition',
  deck_view_read: 'acquisition',
  deck_finished: 'acquisition',
  deck_session_report: 'acquisition',
}

// ── per-event property shapes ────────────────────────────────────────────────
// Only the events whose properties are load-bearing get a shape. A step event
// with no step id is noise, so those properties are REQUIRED — the type makes
// you pass them. Everything else stays a free bag; over-typing analytics is how
// instrumentation stops getting added.

/** A step in a multi-step flow. `index` is 0-based so it sorts as a funnel. */
export interface StepProps {
  /** stable id of the step — never the visible label, which will be reworded. */
  step: string
  /** position in the flow, 0-based. Lets PostHog order the drop-off chart. */
  index: number
}

/** Which AI client the person is wiring up (`claude-desktop`, `cursor`, …). */
export interface ClientProps {
  client: string
}

/** Why a surface said no / blew up. Keep it a short stable slug, never a message. */
export interface ReasonProps {
  reason: string
}

/**
 * One view of the investor deck, and the attention it actually held.
 *
 * `seconds` is VISIBLE seconds — the observer stops the clock when the tab is
 * hidden and restarts it when it comes back. Without that, "how long did they
 * stay on the pricing slide" answers "how long was the laptop open".
 */
export interface DeckViewProps {
  /** the view's stable key (`how`, `model`, `ask`) — never its visible label. */
  view: string
  /** the space that view belongs to (`business`, `story`, `round`). */
  space: string
  /** position in the flat walk, 0-based, so views sort in reading order. */
  index: number
  /** visible seconds spent on this view before leaving it. */
  seconds: number
}

/**
 * Events with a required property shape. Anything absent from this map accepts
 * an optional free-form bag.
 */
export interface EventPropertyMap {
  onboarding_step_viewed: StepProps
  onboarding_step_completed: StepProps
  onboarding_skipped: StepProps
  connect_client_selected: ClientProps
  connect_failed: ClientProps & Partial<ReasonProps>
  feature_blocked: ReasonProps
  error_shown: ReasonProps
  empty_state_viewed: ReasonProps
  deck_view_read: DeckViewProps
}

/** The property argument for a given event — required where the map says so. */
export type PropsFor<E extends AnalyticsEvent> = E extends keyof EventPropertyMap
  ? [properties: EventPropertyMap[E] & AnalyticsProperties]
  : [properties?: AnalyticsProperties]

/**
 * The shape every wrapper in the codebase implements. Written as a rest tuple so
 * the events that need properties demand them and the rest stay one-liners.
 */
export type TypedTrack = <E extends AnalyticsEvent>(event: E, ...args: PropsFor<E>) => void

/**
 * Wrap any raw `(name, props)` sink in the taxonomy's types.
 *
 * A factory rather than a concrete function because the two ends of the product
 * have different sinks — posthog-js in the browser, posthog-node on the server —
 * but must not have different vocabularies. Both build from here.
 */
export function createTypedTrack(
  sink: (event: string, properties?: AnalyticsProperties) => void,
): TypedTrack {
  return ((event: AnalyticsEvent, properties?: AnalyticsProperties) => {
    sink(event, properties)
  }) as TypedTrack
}

/**
 * Stamp the consistent funnel properties onto an event's own properties.
 *
 * Shared by the browser and the server wrapper so a `memory_captured` fired from
 * a dashboard click and one fired from an MCP tool call carry the same
 * `funnel_stage` and land in the same report. Explicit properties always win —
 * a caller can override the stage if it genuinely means something else.
 */
export function funnelProps(
  event: AnalyticsEvent,
  properties?: AnalyticsProperties,
): AnalyticsProperties {
  return { funnel_stage: EVENT_STAGE[event], ...properties }
}

/**
 * Runtime check for an event name that arrived as a plain string — from an MCP
 * payload, a URL, a queue. The type system stops at the process boundary; this
 * is how the server side refuses to invent a name it doesn't know.
 */
export function isAnalyticsEvent(name: string): name is AnalyticsEvent {
  return Object.prototype.hasOwnProperty.call(EVENT_STAGE, name)
}
